import { useEffect, useState } from 'react';
import { X, Play } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getMedia } from '../lib/api';

const MediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const res = await getMedia();
        setMedia(res.data);
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  const photos = media.filter(m => m.type === 'photo');
  const videos = media.filter(m => m.type === 'video');

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="media-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Our Work</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Photos & Media</h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">
            Take a look at some of the amazing events we've been a part of.
          </p>
        </div>
      </section>

      {/* Photos Section */}
      {photos.length > 0 && (
        <section className="py-12 sm:py-16 md:py-24 bg-neutral-50" data-testid="photos-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-8 sm:mb-12" style={{ fontFamily: 'Syne, sans-serif' }}>Photos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {photos.map((photo) => (
                <div 
                  key={photo.id}
                  className="overflow-hidden cursor-pointer aspect-square bg-neutral-200 group"
                  onClick={() => setSelectedImage(photo)}
                  data-testid={`photo-${photo.id}`}
                >
                  <img 
                    src={photo.image_data || photo.thumbnail} 
                    alt={photo.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Videos Section */}
      {videos.length > 0 && (
        <section className="py-12 sm:py-16 md:py-24 bg-white" data-testid="videos-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-8 sm:mb-12" style={{ fontFamily: 'Syne, sans-serif' }}>Videos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {videos.map((video) => {
                const videoId = getYouTubeId(video.url);
                const thumbnail = video.thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null);
                
                return (
                  <div 
                    key={video.id}
                    className="cursor-pointer group relative aspect-video bg-neutral-200"
                    onClick={() => setSelectedVideo(video)}
                    data-testid={`video-${video.id}`}
                  >
                    {thumbnail && (
                      <img 
                        src={thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-900 ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white font-semibold text-sm sm:text-base line-clamp-2">{video.title}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {photos.length === 0 && videos.length === 0 && (
        <section className="py-16 sm:py-24 md:py-32 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center py-12 sm:py-16">
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500">No media available yet. Check back soon!</p>
          </div>
        </section>
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="image-lightbox"
        >
          <button 
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white hover:text-neutral-300 z-10"
            onClick={() => setSelectedImage(null)}
          >
            <X size={28} />
          </button>
          <img 
            src={selectedImage.image_data || selectedImage.thumbnail}
            alt={selectedImage.title}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 text-white font-semibold text-sm sm:text-base text-center px-4">
            {selectedImage.title}
          </p>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
          data-testid="video-lightbox"
        >
          <button 
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white hover:text-neutral-300 z-10"
            onClick={() => setSelectedVideo(null)}
          >
            <X size={28} />
          </button>
          <div 
            className="w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            {getYouTubeId(selectedVideo.url) && (
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(selectedVideo.url)}?autoplay=1`}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MediaPage;
